<template>
    <Spinner v-if="displaySpinner" />
    <div class="form-container">
        <div class="field">
            <label for="name">Name</label>
            <InputText
                id="name"
                autofocus
                v-model="newName"
                required
                :value="newName"
            />
        </div>
        <div class="field">
            <label for="description">Short Description</label>
            <Textarea
                id="description"
                v-model="shortDescription"
                rows="3"
                placeholder="Briefly describe this domain's purpose (optional)"
                autoResize
                class="w-full"
            />
        </div>
        <div class="buttons">
            <Button label="Cancel" text severity="secondary" @click="cancel" />
            <Button label="Save" severity="primary" @click="closeAndSave" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { Ref, computed, inject, ref } from "vue";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Spinner from "@/shared/Spinner.vue";
import { useDomainsManager, useDomainsStore } from "@/stores/domains-store";
import { useHotKeys } from "@/composables/hotkeys";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";

const newName = ref("");
const shortDescription = ref("");
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const { create } = useDomainsStore();
const { isLoading } = useDomainsManager();
const { addHotKey } = useHotKeys();

const cancel = () => dialogRef?.value.close();
const closeAndSave = async () => {
    await create(newName.value, shortDescription.value);
    dialogRef?.value?.close();
};

const displaySpinner = computed(() => {
    return isLoading.value;
});

/**
 * Save on cmd/ctrl + enter.
 */
addHotKey("ControlOrMeta+Enter", async () => {
    await closeAndSave();
});
</script>

<style lang="scss" scoped>
.form-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.field {
    width: 100%;

    label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
    }

    input,
    textarea {
        width: 100%;
    }
}

.buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
}
</style>
